/**
 * TagChip — UX §3.9. Lowercase, no icon, optional X for removal.
 */
import { X } from '@phosphor-icons/react';
import { cn } from '../../lib/cn';

export interface TagChipProps {
  label: string;
  active?: boolean;
  removable?: boolean;
  onRemove?: () => void;
  onClick?: () => void;
  className?: string;
}

const BASE = cn(
  'inline-flex items-center gap-1.5',
  'px-3 py-1',
  'font-sans text-xs lowercase',
  'border rounded-full',
  'transition-colors duration-200 ease-out-slow',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
);

export function TagChip({
  label,
  active,
  removable,
  onRemove,
  onClick,
  className,
}: TagChipProps) {
  const clickable = Boolean(onClick);
  const Cmp = clickable ? 'button' : 'span';
  // Truncate >20 chars per UX §7.3.
  const displayLabel = label.length > 20 ? `${label.slice(0, 19)}…` : label;
  return (
    <Cmp
      type={clickable ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        BASE,
        active
          ? 'bg-accent-soft text-accent border-accent'
          : 'bg-transparent text-ink-700 border-ink-200',
        clickable && 'hover:bg-surface-2',
        className,
      )}
      title={label.length > 20 ? label : undefined}
    >
      <span>{displayLabel}</span>
      {removable && onRemove ? (
        <button
          type="button"
          aria-label={`Remove tag ${label}`}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="text-ink-500 hover:text-ink-900 focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
        >
          <X size={10} weight="regular" aria-hidden />
        </button>
      ) : null}
    </Cmp>
  );
}
